/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import './Assessment.css';

const API = process.env.REACT_APP_BACKEND_URL;

const PATHWAY_NAMES = {
  pathway_a: { letter: '', name: 'Founders Pathway', color: '#1E491A' },
  pathway_b: { letter: '', name: 'Employment Pathway', color: '#1E491A' },
  pathway_c: { letter: '', name: 'Independent Pathway', color: '#1E491A' },
};

export default function AssessmentRegister() {
  const { attemptId } = useParams();
  const [searchParams] = useSearchParams();
  const chosenPathway = searchParams.get('pathway') || 'pathway_a';
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const pw = PATHWAY_NAMES[chosenPathway] || PATHWAY_NAMES.pathway_a;

  // Pre-fill from assessment data
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/free-assessment/${attemptId}/results`);
        const data = res.data;
        setEmail(data.email || '');
        const q1 = data.answers?.Q1;
        if (typeof q1 === 'object' && q1) {
          setName(q1.full_name || q1.preferred_name || '');
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [attemptId]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Register via the portal bridge endpoint
      const res = await axios.post(`${API}/api/free-assessment/${attemptId}/register`, {
        email,
        name,
        password,
        chosen_pathway: chosenPathway,
      });
      // Auto-login with token from registration
      loginWithToken(res.data.token, res.data.user);
      toast.success(`Welcome to SPRING, ${name.split(' ')[0]}! You're on ${pw.name}.`);
      navigate('/choose-coach');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Registration failed';
      if (detail.includes('already registered')) {
        setError('This email already has an account. Try logging in instead.');
      } else {
        setError(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="assess-completing">
        <div className="assess-completing-inner">
          <div className="assess-spinner" />
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="assess-register-page" data-testid="assessment-register">
      <div className="assess-register-inner">
        <div className="assess-register-badge" style={{ background: pw.color }}>
          Pathway {pw.letter} — {pw.name}
        </div>

        <h1>Let's set you up.</h1>
        <p className="assess-register-subtitle">
          Your assessment is complete and your results are saved. Create your account to begin your journey on {pw.name}.
        </p>

        <form onSubmit={handleRegister} className="assess-register-form">
          <div className="assess-field-group">
            <label>Email</label>
            <input type="email" value={email} readOnly className="assess-register-readonly" data-testid="register-email" />
          </div>

          <div className="assess-field-group">
            <label>Full name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Your name" required data-testid="register-name" />
          </div>

          <div className="assess-field-group">
            <label>Create a password</label>
            <div className="assess-pw-wrap">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                minLength={6}
                data-testid="register-password"
              />
              <button type="button" className="assess-pw-toggle" onClick={() => setShowPw(!showPw)}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && <p className="assess-error" data-testid="register-error">{error}</p>}

          <button type="submit" className="assess-btn-primary" disabled={submitting} data-testid="register-submit">
            {submitting ? 'Creating account...' : 'Create my account'}
            {!submitting && <ArrowRight size={18} />}
          </button>

          <p className="assess-register-login-link">
            Already have an account? <a href="/auth" onClick={e => { e.preventDefault(); navigate('/auth'); }}>Log in</a>
          </p>
        </form>
      </div>
    </div>
  );
}
